<template>
  <div class="uk-container">
    <div class="progress" v-html="progress" />
    <transition name="fade" mode="out-in" @before-enter="beforeEnter">
      <div v-if="!loading">
        <status :appointment="statusData" :visibility="componentData.statusVisibility" />
        <component :is="current" v-bind="componentData" />
      </div>
    </transition>
  </div>
</template>

<script>
import Status from '@/components/appointment/Status.vue'
import Emergency from '@/components/appointment/Emergency.vue'
import Dentist from '@/components/appointment/Dentist.vue'
import Customer from '@/components/appointment/Customer.vue'
import Payment from '@/components/appointment/Payment.vue'
import Notifications from '@/components/appointment/Notifications.vue'
import Result from '@/components/appointment/Result.vue'

export default {
  name: 'ViewAppointmentCreate',

  components: {
    Status,
    Emergency,
    Dentist,
    Customer,
    Payment,
    Notifications,
    Result
  },

  data() {
    return {
      loading: true,
      errors: [],
      current: 'Emergency',
      enquiry: null,
      statusData: null,
      appointment: {
        id: null,
        dentist: {
          id: null,
          date: null,
          bit: null
        },
        customer: {
          location: null,
          name: {
            first: null,
            last: null
          },
          contact: {
            phone: null,
            email: null
          }
        },
        emergency: {
          description: null,
          complaint: null,
          features: []
        },
        notes: null,
        payment: {
          amount: null
        }
      }
    }
  },

  computed: {
    componentData() {
      const { current } = this
      const response = {}

      if (current === 'Emergency' && this.enquiry) {
        response.description = this.enquiry.content.details
      }

      if (current === 'Dentist') {
        response.features = this.appointment.emergency.features

        if (this.enquiry) {
          response.customerLocation = this.enquiry.content.location.customer
        }
      }

      if (current === 'Customer' && this.enquiry) {
        response.nameFirst = this.enquiry.content.name.first
        response.nameLast = this.enquiry.content.name.last
        response.contactPhone = this.enquiry.content.contact.phone
        response.contactEmail = this.enquiry.content.contact.email
      }

      if (current === 'Payment') {
        response.appointment = this.appointment.id
        response.secret = this.appointment.payment.client_secret
        response.type = 'new'
      }

      if (current === 'Notifications') {
        response.appointmentId = this.appointment.id
      }

      if (current === 'Result') {
        response.statusVisibility = false
        response.appointmentId = this.appointment.id
      }

      return response
    },

    progress() {
      const steps = ['Emergency', 'Dentist', 'Customer', 'Payment', 'Notifications', 'Result']

      return `${steps.indexOf(this.current) + 1}/${steps.length}`
    }
  },

  created() {
    if (this.$route.query && this.$route.query.e) {
      const enquiry = this.$store.getters['enquiries/find'](this.$route.query.e)

      if (enquiry) {
        this.enquiry = enquiry
      }
    }

    this.$bus.$on('appointment-error', (data) => {
      this.error(data)
    })

    this.$bus.$on('appointment-section-complete', (data) => {
      this.sectionComplete(data)
    })

    this.$bus.$on('appointment-restart', () => {
      this.restart()
    })

    this.statusData = JSON.parse(JSON.stringify(this.appointment))
    this.loading = false
  },

  beforeDestroy() {
    this.$bus.$off('appointment-error')
    this.$bus.$off('appointment-section-complete')
    this.$bus.$off('appointment-restart')
  },

  methods: {
    beforeEnter() {
      document.querySelector('#content').scrollTo(0, 0)
    },

    error(data) {
      // data.timestamp = this.$moment()
      this.errors.push(data)

      this.$uikit.notification({
        message: data.message,
        status: data.status,
        timeout: 15000
      })
    },

    async sectionComplete(data) {
      const { type } = data
      let next = null
      let result = null

      if (type === 'emergency') {
        result = await this.saveEmergency(data.data)
        next = 'Dentist'
      }

      if (type === 'dentist') {
        result = await this.saveDentist(data.data)
        next = 'Customer'
      }

      if (type === 'customer') {
        result = await this.saveCustomer(data.data)
        next = 'Payment'
      }

      if (type === 'payment') {
        // Payment is processed through Stripe and DB updates are handled inside component
        result = { status: true }
        this.errors = []
        next = 'Notifications'
      }

      if (type === 'notifications') {
        result = await this.sendNotifications(data.data)
        next = 'Result'
      }

      if (result.status) {
        this.switchSection(next)
      } else {
        this.error({ type: 'db', message: result.error, status: 'danger' })
      }
    },

    switchSection(section) {
      this.loading = true

      setTimeout(() => {
        this.statusData = JSON.parse(JSON.stringify(this.appointment))
        this.current = section
      }, 250)

      setTimeout(() => { this.loading = false }, 250)
    },

    saveEmergency(data) {
      this.appointment.emergency = data.emergency

      if (this.appointment.emergency.features.length === 0) {
        this.appointment.emergency.features.length = null
      }

      this.appointment.payment.amount = data.price

      return { status: true }
    },

    saveDentist({ selected, location }) {
      this.appointment.dentist.id = selected.dentist
      this.appointment.dentist.date = selected.date.t
      this.appointment.dentist.bit = selected.bit
      this.appointment.customer.location = `${location.info.outcode} ${location.info.incode}`

      return { status: true }
    },

    async saveCustomer(data) {
      const loader = this.$loader.show('Storing Appointment Information')
      let response = null
      const customer = {
        name: {
          first: data.customer.fname,
          last: data.customer.lname
        },
        contact: {
          email: data.customer.email,
          phone: data.customer.phone
        }
      }

      this.appointment.customer = { ...this.appointment.customer, ...customer }
      this.appointment.notes = data.notes

      const createAppointmentStore = this.$firebaseFunctions.httpsCallable('createAppointmentStore')
      const appointment = {
        dentist: this.appointment.dentist,
        customer: this.appointment.customer,
        emergency: this.appointment.emergency,
        price: this.appointment.payment.amount,
        notes: this.appointment.notes
      }

      if (this.enquiry) {
        appointment.enquiry = this.enquiry.id
      }

      try {
        const result = await createAppointmentStore(appointment)
        this.appointment.id = result.data.doc
        this.appointment.payment = result.data.payment

        await loader.hide()
        response = { status: true }
      } catch (error) {
        loader.hide(true)
        response = { status: false, error: error.message }
      }

      return response
    },

    async sendNotifications(notifications) {
      const loader = this.$loader.show('Finalizing the Booking')
      const createAppointmentComplete = this.$firebaseFunctions.httpsCallable('createAppointmentNotifications')
      let response = null
      const data = {
        notifications,
        appointment: this.appointment.id
      }

      try {
        await createAppointmentComplete(data)
        await loader.hide()
        response = { status: true }
      } catch (error) {
        loader.hide(true)
        response = { status: false, error: error.message }
      }

      return response
    },

    restart() {
      this.errors = []
      this.appointment = {
        id: null,
        dentist: {
          id: null,
          date: null,
          bit: null
        },
        customer: {
          location: null,
          name: {
            first: null,
            last: null
          },
          contact: {
            phone: null,
            email: null
          }
        },
        emergency: {
          description: null,
          complaint: null,
          features: []
        },
        notes: null,
        payment: {
          amount: null
        }
      }

      this.switchSection('Emergency')
    }
  },

  beforeRouteLeave(to, from, next) {
    if (this.current === 'Result') {
      next()
    } else {
      this.$uikit.modal.confirm('Are you sure that you want to abandon the appointment booking process?').then(() => {
        next()
      }, () => {
        next(false)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.error {
  z-index: 2;
}

.progress {
  position: absolute;
  top: 0;
  right: 0;
  padding: .5em;
  background-color: $colour-secondary;
  color: $colour-light;
  font-size: .75em;
  font-weight: bold;
}
</style>
